import React, { useEffect, useState } from "react";
import { Loader2, ExternalLink, MapPin, Landmark, DollarSign, ListFilter } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";
import { motion } from "framer-motion";

function VcLogo({ domain, name, size = 48 }) {
  const [err, setErr] = useState(false);
  if (err || !domain) {
    return (
      <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-[#7C3AED] font-display font-semibold flex items-center justify-center border border-indigo-100 shrink-0">
        {name[0]}
      </div>
    );
  }
  return (
    <img
      src={`https://logo.clearbit.com/${domain}`}
      alt={`${name} logo`}
      onError={() => setErr(true)}
      style={{ width: size, height: size }}
      className="rounded-2xl object-contain bg-white border border-gray-150 p-1.5 shrink-0 shadow-sm"
    />
  );
}

export default function VcFirmsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [activeFilters, setActiveFilters] = useState({
    location: "All",
    focus: "All",
    ticketSize: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/vc-firms?limit=50")
      .then((r) => {
        if (alive) {
          // Enrich VC Firms
          const enriched = (r.data.items || []).map((vc, index) => {
            const focuses = [
              "SaaS, Fintech, Consumer Tech",
              "B2B SaaS, Enterprise Software, DevTools",
              "Consumer Brands, Marketplace, D2C",
              "DeepTech, AI/ML, ClimateTech",
              "Healthcare, BioTech, AgTech",
            ];
            const sizes = [
              "$500K - $2M",
              "$1M - $5M",
              "$2M - $10M",
              "$100K - $1M",
              "$5M - $20M"
            ];
            const portfolios = [
              ["Razorpay", "Zepto", "CRED"],
              ["Darwinbox", "Apna", "Yellow.ai"],
              ["boAt", "Slice", "Jar"],
              ["Ola Krutrim", "Sarvam AI", "Lumen Labs"],
              ["Physics Wallah", "Delhivery", "Meesho"]
            ];

            return {
              ...vc,
              focus: focuses[index % focuses.length],
              ticketSize: sizes[index % sizes.length],
              portfolio: portfolios[index % portfolios.length],
            };
          });
          setItems(enriched);
        }
      })
      .catch((e) => console.error("Error loading VCs:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const getFilterOptions = (key) => {
    const vals = items.map((item) => {
      if (key === "location") return item.location ? item.location.split(",")[0].trim() : "";
      return item[key];
    }).filter(Boolean);
    return Array.from(new Set(vals)).sort();
  };

  const locationsList = getFilterOptions("location");
  const focusList = [
    "SaaS, Fintech, Consumer Tech",
    "B2B SaaS, Enterprise Software, DevTools",
    "Consumer Brands, Marketplace, D2C",
    "DeepTech, AI/ML, ClimateTech",
    "Healthcare, BioTech, AgTech",
  ];
  const ticketSizesList = ["$100K - $1M", "$500K - $2M", "$1M - $5M", "$2M - $10M", "$5M - $20M"];

  const filters = [
    { key: "location", label: "Location", options: locationsList },
    { key: "focus", label: "Investment Focus", options: focusList },
    { key: "ticketSize", label: "Ticket Size", options: ticketSizesList },
  ];

  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.focus && item.focus.toLowerCase().includes(q));

    const matchesLocation =
      activeFilters.location === "All" ||
      (item.location && item.location.toLowerCase().includes(activeFilters.location.toLowerCase()));

    const matchesFocus =
      activeFilters.focus === "All" || item.focus === activeFilters.focus;

    const matchesTicketSize =
      activeFilters.ticketSize === "All" || item.ticketSize === activeFilters.ticketSize;

    return matchesSearch && matchesLocation && matchesFocus && matchesTicketSize;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Indian VC Firms"
      subtitle="Venture capital firms and institutional funds backing Indian entrepreneurs from early-stage seed to growth rounds."
    >
      <div className="space-y-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search VC firms by name, investment focus, location..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} VC firms
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Grid List */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No venture capital firms match your search.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedItems.map((vc) => (
                <motion.div
                  key={vc.slug}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-[24px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 group"
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <VcLogo domain={vc.domain} name={vc.name} />
                      <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-3 py-1 rounded-full flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {vc.location || "India"}
                      </span>
                    </div>

                    <div>
                      <h3 className="font-display font-bold text-gray-900 text-lg leading-snug group-hover:text-[#7C3AED] transition">
                        {vc.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-3">
                        {vc.description}
                      </p>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-gray-100/60">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Landmark className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        <span className="font-medium text-gray-500">Focus:</span>
                        <span className="font-bold text-gray-800 line-clamp-1">{vc.focus}</span>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        <span className="font-medium text-gray-500">Ticket Size:</span>
                        <span className="font-bold text-emerald-600">{vc.ticketSize}</span>
                      </div>

                      <div className="flex items-start gap-2 text-xs text-gray-600">
                        <ListFilter className="w-3.5 h-3.5 text-pink-500 mt-0.5 shrink-0" />
                        <div>
                          <span className="font-medium text-gray-500">Key Portfolio:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {vc.portfolio && vc.portfolio.map((port) => (
                              <span key={port} className="text-[9px] font-bold px-2 py-0.5 bg-gray-50 text-gray-600 rounded border border-gray-100">
                                {port}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <a
                      href={vc.website || `https://${vc.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-gray-400 hover:text-[#7C3AED] transition flex items-center gap-1"
                    >
                      Website <ExternalLink className="w-3.5 h-3.5" />
                    </a>

                    <a
                      href={vc.website || `https://${vc.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#7C3AED] hover:underline"
                    >
                      View Profile →
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
